import {Subscription} from "rxjs";
import {Component, EventEmitter, inject, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";

import {ToastrService} from "ngx-toastr";
import {NgSelectComponent} from "@ng-select/ng-select";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

import {UserDefaultAddressApiService} from "@numizmat/services/core";
import {UpsertUserDefaultAddressRequest} from "@numizmat/services-models/core";
import {MunicipalityResponse, PopulatedPlaceResponse, RegionResponse} from "@numizmat/services-models/nomenclature";
import {MunicipalityApiService, PopulatedPlaceApiService, RegionApiService} from "@numizmat/services/nomenclature";

@Component({
  selector: 'nz-user-default-address-modal',
  standalone: true,
  imports: [
    FormsModule,
    NgSelectComponent,
    ReactiveFormsModule
  ],
  templateUrl: './user-default-address-modal.component.html',
  styleUrl: './user-default-address-modal.component.scss'
})
export class UserDefaultAddressModalComponent implements OnInit, OnDestroy {
  @Output() addressChange: EventEmitter<any> = new EventEmitter<any>();

  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private toastrService: ToastrService = inject(ToastrService);
  private regionApiService: RegionApiService = inject(RegionApiService);
  private municipalityApiService: MunicipalityApiService = inject(MunicipalityApiService);
  private populatedPlaceApiService: PopulatedPlaceApiService = inject(PopulatedPlaceApiService);
  private userDefaultAddressApiService: UserDefaultAddressApiService = inject(UserDefaultAddressApiService);

  private subs: Subscription = new Subscription();

  protected currentRegionId?: number;
  protected currentMunicipalityId?: number;
  protected currentPopulatedPlaceId?: number;

  protected loading: boolean = false;
  protected form: FormGroup = this.initializeForm();
  protected regions: RegionResponse[] = [];
  protected municipalities: MunicipalityResponse[] = [];
  protected populatedPlaces: PopulatedPlaceResponse[] = [];

  public ngOnInit(): void {
    this.createRegionIdValueChangesSubscription();
    this.createMunicipalityIdValueChangesSubscription();
    this.getAllRegions();

    if (!!this.currentRegionId) {
      this.regionId?.setValue(this.currentRegionId);
      this.municipalityId?.setValue(this.currentMunicipalityId);
      this.populatedPlaceId?.setValue(this.currentPopulatedPlaceId);
      this.municipalityId?.enable();
      this.populatedPlaceId?.enable();
    }
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onSubmit(): void {
    this.loading = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.loading = false;
      return;
    }
    const formValue = this.form.getRawValue();
    const request: UpsertUserDefaultAddressRequest = {
      regionId: formValue.regionId,
      municipalityId: formValue.municipalityId,
      populatedPlaceId: formValue.populatedPlaceId
    };
    this.userDefaultAddressApiService.updateUserDefaultAddress(request).subscribe({
      next: response => {
        this.loading = false;
        this.toastrService.success("Успешно променихте адреса по подразбиране.");
        const populatedPlaceName = this.populatedPlaces.find(x => x.populatedPlaceId === formValue.populatedPlaceId)?.populatedPlaceName;
        this.addressChange.emit({
          regionId: formValue.regionId,
          municipalityId: formValue.municipalityId,
          populatedPlaceId: formValue.populatedPlaceId,
          populatedPlaceName: populatedPlaceName
        })
        this.closeModal();
      }
    });
  }

  protected closeModal(): void {
    this.activeModal.dismiss();
  }

  protected onChangeRegionId(region: number | undefined): void {
    this.municipalityId?.setValue(null);
    this.populatedPlaceId?.setValue(null);
    this.municipalityId?.disable();
    this.populatedPlaceId?.disable();
    this.municipalities = [];
    this.populatedPlaces = [];
    if (!!region) {
      this.municipalityId?.enable();
    }
  }

  protected onChangeMunicipalityId(municipality: number | undefined): void {
    this.populatedPlaceId?.setValue(null);
    if (!!municipality) {
      this.populatedPlaceId?.enable();
    } else {
      this.populatedPlaceId?.disable();
      this.populatedPlaces = [];
    }
  }

  private getAllRegions() {
    this.regionApiService.getAllRegions().subscribe({
      next: response => {
        this.regions = response;
      }
    });
  }

  private getMunicipalitiesByRegionId(regionId: number): void {
    this.municipalityApiService.getMunicipalitiesByRegionId(regionId).subscribe({
      next: response => {
        this.municipalities = response;
      }
    });
  }

  private getPopulatedPlacesByMunicipalityId(municipalityId: number): void {
    this.populatedPlaceApiService.getPopulatedPlacesByMunicipalityId(municipalityId).subscribe({
      next: response => {
        this.populatedPlaces = response;
      }
    });
  }

  private createRegionIdValueChangesSubscription(): void {
    const subscription = this.regionId?.valueChanges.subscribe({
      next: (value: number | undefined) => {
        if (!!value) {
          this.getMunicipalitiesByRegionId(value);
        }
      }
    });
    this.subs.add(subscription);
  }

  private createMunicipalityIdValueChangesSubscription(): void {
    const subscription = this.municipalityId?.valueChanges.subscribe({
      next: (value: number | undefined) => {
        if (!!value) {
          this.getPopulatedPlacesByMunicipalityId(value);
        }
      }
    });
    this.subs.add(subscription);
  }

  private initializeForm() {
    return new FormGroup({
      regionId: new FormControl(null, [Validators.required]),
      municipalityId: new FormControl({value: null, disabled: true}, [Validators.required]),
      populatedPlaceId: new FormControl({value: null, disabled: true}, [Validators.required]),
    });
  }

  get regionId() {
    return this.form.get('regionId');
  }

  get municipalityId() {
    return this.form.get('municipalityId');
  }

  get populatedPlaceId() {
    return this.form.get('populatedPlaceId');
  }
}
